import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { StudentMapping, XlsxImportEntry } from '../types/XlsxImportTypes';
import styles from './_XlsxImportForms.module.scss';
import { XlsxStudentMappingFormRow } from './XlsxStudentMappingFormRow';

type XlsxStudentMappingFormProps = {
  students: { uuid: string; name: string }[];
  newEntries: XlsxImportEntry<StudentMapping>[];
  matchedEntries: XlsxImportEntry<StudentMapping>[];
  onEntryChanged: (entries: XlsxImportEntry<StudentMapping>) => void;
};

export const XlsxStudentMappingForm = ({
  students,
  newEntries,
  matchedEntries,
  onEntryChanged,
}: XlsxStudentMappingFormProps) => {
  const { t } = useTranslation();

  const availableProfiles: { uuid: string; name: string }[] = useMemo(() => {
    const connectedProfileUuids = matchedEntries
      .map((e) => e.connectProfileUuid)
      .filter((uuid): uuid is string => !!uuid);
    return students
      .filter((p) => p && !connectedProfileUuids.includes(p.uuid))
      .map((p) => ({ uuid: p.uuid, name: p.name ?? '' }));
  }, [matchedEntries, students]);

  return (
    <div className={styles['xlsx-mapping-form']}>
      {newEntries.length > 0 && (
        <div className={styles.headline}>{t('import.mapType.new', { type: t('students.title', { count: 2 }) })}</div>
      )}
      {newEntries.map((entry, id) => {
        return (
          <XlsxStudentMappingFormRow
            key={entry.rowIndex}
            id={id}
            entry={entry}
            profiles={availableProfiles}
            selectedProfile={null}
            onProfileSelect={(profileUuid) => {
              const _newEntry = { ...entry, connectProfileUuid: profileUuid };
              onEntryChanged(_newEntry);
            }}
          />
        );
      })}
      {matchedEntries.length > 0 && (
        <div className={styles.headline}>{t('import.mapType.update', { type: t('students.title', { count: 2 }) })}</div>
      )}
      {matchedEntries.map((entry, id) => {
        const selectedProfile = students.find((p) => p.uuid === entry.connectProfileUuid);

        return (
          <XlsxStudentMappingFormRow
            key={entry.rowIndex}
            id={id}
            entry={entry}
            profiles={availableProfiles}
            selectedProfile={selectedProfile ?? null}
            onProfileSelect={(profileUuid) => {
              const _newEntry = { ...entry, connectProfileUuid: profileUuid };
              onEntryChanged(_newEntry);
            }}
          />
        );
      })}
    </div>
  );
};
